(() => {
  const VERSION = '1.0';
  const pageOnce = new Set();

  const isPreview = () => {
    const params = new URLSearchParams(window.location.search);
    return document.documentElement.classList.contains('shopify-design-mode')
      || window.Shopify?.designMode === true
      || params.has('preview_theme_id')
      || window.Shopify?.theme?.role === 'unpublished';
  };

  const analyticsAllowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return typeof privacy?.analyticsProcessingAllowed === 'function'
        ? privacy.analyticsProcessingAllowed()
        : true;
    } catch {
      return true;
    }
  };

  const debugEnabled = () => new URLSearchParams(window.location.search).get('zyma_analytics_debug') === '1';

  const deviceBucket = () => {
    const width = window.innerWidth || document.documentElement.clientWidth || 0;
    if (width <= 375) return 'small_mobile';
    if (width <= 749) return 'mobile';
    if (width <= 989) return 'tablet';
    return 'desktop';
  };

  const compact = (input) => Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

  const common = () => {
    const body = document.body;
    return compact({
      zyma_event_version: VERSION,
      zyma_page_type: body?.dataset.zymaPageType,
      zyma_template: body?.dataset.zymaTemplate,
      zyma_locale: body?.dataset.zymaLocale || document.documentElement.lang,
      zyma_device: deviceBucket(),
      zyma_product_id: body?.dataset.productId,
      zyma_product_handle: body?.dataset.productHandle,
    });
  };

  const track = (name, params = {}) => {
    const event = String(name || '').startsWith('zyma_') ? String(name) : `zyma_${name}`;
    const payload = compact({ event, ...common(), ...params });

    if (isPreview() || !analyticsAllowed()) {
      if (debugEnabled()) console.info('[ZYMA analytics blocked]', payload);
      return false;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    if (debugEnabled()) console.info('[ZYMA analytics]', payload);
    return true;
  };

  const trackOnce = (key, name, params = {}) => {
    if (pageOnce.has(key)) return false;
    pageOnce.add(key);
    return track(name, params);
  };

  const expose = (experimentId, variant, params = {}) => {
    const id = String(experimentId || '').trim();
    const arm = String(variant || '').trim();
    if (!id || !arm) return false;
    const key = `zyma_exposure:${id}:${arm}`;
    try {
      if (window.sessionStorage.getItem(key) === '1') return false;
      window.sessionStorage.setItem(key, '1');
    } catch {}
    return track('experiment_exposure', {
      zyma_experiment_id: id,
      zyma_experiment_variant: arm,
      ...params,
    });
  };

  window.ZymaAnalytics = Object.freeze({ version: VERSION, track, trackOnce, expose });

  document.addEventListener('zyma:track', (event) => {
    const detail = event instanceof CustomEvent ? event.detail : null;
    if (!detail?.event) return;
    track(detail.event, detail.data || {});
  });

  const productHandleFromUrl = (href) => {
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch {
      return '';
    }
  };

  const purchaseContext = (element) => {
    const component = element.closest('product-form-component')
      || document.querySelector('[data-zyma-pd-buy] product-form-component,.zyma-purchase-engine product-form-component,.zyma-pdp-purchase product-form-component,.zyma-bd product-form-component,.zyma-bundle-pdp product-form-component,.zbp2 product-form-component');
    const form = component?.querySelector('form');
    const pdChoice = form?.querySelector('[data-zyma-pd-choice]:checked');
    const nativeChoice = form?.querySelector('.zyma-native-choice-input:checked');
    const choice = pdChoice || nativeChoice;
    let units = Math.max(1, Number(choice?.dataset.units || 1) || 1);
    let mode = '';

    if (pdChoice) {
      if (pdChoice.hasAttribute('data-bulk')) {
        units = Math.max(units, Number(form?.querySelector('[data-zyma-pd-step-input]')?.value || units) || units);
        mode = 'bulk';
      } else {
        mode = units > 1 ? 'multipack' : 'single';
      }
    } else if (nativeChoice) {
      const engine = element.closest('.zyma-purchase-engine');
      mode = engine?.classList.contains('is-offers-mode') ? 'multipack' : 'quantity';
      if (mode === 'quantity') {
        units = Math.max(1, Number(engine?.querySelector('[data-zyma-native-quantity]')?.value || units) || units);
      }
    }

    const variantInput = form?.querySelector('[name="id"]:checked') || form?.querySelector('[name="id"]');
    const quantityInput = form?.querySelector('[name="quantity"]');
    // Bundler injects its own tier radios but submits the canonical product ID
    // and a real quantity in this Shopify form. Read that actual quantity.
    const isBundler = !!form?.closest('[data-zyma-bundler-native]');
    if (isBundler) {
      units = Math.max(1, Math.round(Number(quantityInput?.value || 1) || 1));
      mode = units > 1 ? 'multipack' : 'single';
    }
    return compact({
      zyma_product_id: component?.dataset.productId || document.body?.dataset.productId,
      zyma_product_handle: document.body?.dataset.productHandle,
      zyma_variant_id: variantInput?.value,
      zyma_quantity: Math.max(1, Number(quantityInput?.value || 1) || 1),
      zyma_units: units,
      zyma_purchase_mode: mode,
    });
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;

    const whatsapp = target.closest('a[href*="wa.me/"],a[href*="whatsapp.com/"]');
    if (whatsapp) {
      track('whatsapp_click', {
        zyma_surface: whatsapp.classList.contains('zyma-whatsapp-float') ? 'floating' : 'link',
      });
    }

    const searchProduct = target.closest('a[href*="/products/"]');
    if (searchProduct && searchProduct.closest('.zyma-srch,.search-modal,.predictive-search')) {
      const surface = searchProduct.closest('.search-modal,.predictive-search') ? 'predictive_search' : 'search_page';
      track('search_to_pdp', {
        zyma_search_surface: surface,
        zyma_destination_product_handle: productHandleFromUrl(searchProduct.href),
      });
    }

    if (!event.isTrusted) return;

    const sticky = target.closest('[data-zyma-pd-bar-submit],.zp-sticky-purchase button');
    if (sticky) {
      track('sticky_atc_click', purchaseContext(sticky));
      return;
    }

    const atc = target.closest('[ref="addToCartButton"]');
    if (!atc) return;
    const context = purchaseContext(atc);

    if (atc.closest('.zyma-basket-extra')) {
      const extra = atc.closest('.zyma-basket-extra');
      track('cart_cross_sell_add', {
        ...context,
        zyma_cross_sell_type: extra?.classList.contains('zyma-basket-extra--offer') ? 'bundle_offer' : 'pairing',
      });
      return;
    }

    if (atc.closest('[data-finder-direct-add]')) {
      track('finder_result_atc', context);
      return;
    }

    if (atc.closest('.zyma-bd,.zyma-bundle-pdp,.zbp2')) {
      track('bundle_atc', { ...context, zyma_purchase_mode: 'curated_bundle' });
      return;
    }

    if (atc.closest('[data-zyma-pd-buy],.zyma-pdp-purchase,.zyma-purchase-engine,.zyma-pd__info')) {
      track('hero_atc_click', context);
    }
  });

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.checked) return;
    // Bundler's live radio names are _bndlr_radio_*; they are not ZYMA-native choices.
    // Read the updated canonical form quantity after the app's change handlers run.
    const appInput = event.target;
    if (appInput.name.startsWith('_bndlr_radio_') || appInput.id.startsWith('_bndlr_radio_')) {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const context = appInput.closest('[data-zyma-pd], [data-zyma-pdp]') || document;
        const quantityInput = context.querySelector('[data-zyma-bundler-form-quantity]');
        if (!quantityInput) return;
        const units = Math.max(1, Math.round(Number(quantityInput.value) || 1));
        track('multipack_option_selected', {
          zyma_product_id: context.dataset?.productId || document.body?.dataset.productId,
          zyma_product_handle: document.body?.dataset.productHandle,
          zyma_units: units,
          zyma_purchase_mode: units > 1 ? 'multipack' : 'single',
          zyma_variant_id: context.querySelector('[data-zyma-canonical-variant]')?.value,
          zyma_source: 'bundler',
        });
      }));
      return;
    }
    const choice = event.target.matches('[data-zyma-pd-choice],.zyma-native-choice-input[data-zyma-native-choice]')
      ? event.target
      : null;
    if (!choice) return;

    let units = Math.max(1, Number(choice.dataset.units || 1) || 1);
    if (choice.hasAttribute('data-bulk')) {
      const form = choice.closest('form');
      units = Math.max(units, Number(form?.querySelector('[data-zyma-pd-step-input]')?.value || units) || units);
    }
    if (units < 2 && !choice.hasAttribute('data-bulk')) return;

    const price = Math.max(0, Number(choice.dataset.price || 0) || 0);
    const compareAt = Math.max(price, Number(choice.dataset.compare || price) || price);
    track('multipack_option_selected', {
      zyma_product_id: choice.dataset.productId || document.body?.dataset.productId,
      zyma_product_handle: document.body?.dataset.productHandle,
      zyma_variant_id: choice.value,
      zyma_units: units,
      zyma_price_cents: price,
      zyma_saving_cents: Math.max(0, compareAt - price),
      zyma_bulk: choice.hasAttribute('data-bulk'),
    });
  });

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    const container = details.closest('.zyma-faq,.zyma-bundle-faq,.zyma-qa,.zyma-pd-accordions,.zyma-pdp-buy-accordions');
    if (!container) return;
    const items = [...container.querySelectorAll('details')];
    track('faq_interaction', {
      zyma_action: 'open',
      zyma_faq_index: Math.max(1, items.indexOf(details) + 1),
      zyma_faq_surface: details.closest('.zyma-pd-accordions,.zyma-pdp-buy-accordions') ? 'pdp_accordion' : 'faq',
    });
  }, true);

  const init = () => {
    if (document.body?.classList.contains('template-search') && document.querySelector('.zyma-srch__empty')) {
      const query = new URLSearchParams(window.location.search).get('q') || '';
      trackOnce('search_zero_result', 'search_zero_result', {
        zyma_search_surface: 'search_page',
        zyma_query_length: query.trim().length,
      });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();